import { defineComponent as _defineComponent } from 'vue'
import { useTranslations } from '@/core/translate.mixin';
import Http from '../../../core/http/http.service';
import QuickOrderTable from '@/ui/views/quickorder/QuickOrderTable.vue';
import QueryParamsService, { QueryParams } from '../../../core/query-params.service';

import { QuickOrderSearchFilter } from '@/enums';
import SearchSuggestViewObject = Vertica.LouisPoulsen.Application.Search.ViewModels.SearchSuggestViewObject;
import QuickOrderSearchField from '@/ui/views/quickorder/QuickOrderSearchField.vue';
import ScrollService from '@/core/scroll.service';
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router/composables';
import { watchRelativeUrl } from '@/core/route.service';

enum QuickOrderResultMode {
    HasResults,
    NoResults,
    LoadingResults,
    NoSearch
}


export default /*#__PURE__*/_defineComponent({
  __name: 'QuickOrder',
  props: {
    pageTitle: null,
    allowPurchase: { type: Boolean },
    isUsMarket: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as {
    pageTitle?: string,
    allowPurchase: boolean,
    isUsMarket?: boolean
}


onMounted(search);

const { t } = useTranslations();

const initialSort = QueryParamsService.getParam(QueryParams.QuickOrderSearchDeliverySort);
const searchTerm = ref<string>(QueryParamsService.getParam(QueryParams.QuickOrderSearchTerm) || '');
const searchProductId = ref<string>(QueryParamsService.getParam(QueryParams.QuickOrderSearchProductId) || '');
const searchDeliverySort = ref<boolean>(initialSort === 'true' || true);

const searchResultPageNumber = ref<number>(parseInt(QueryParamsService.getParam(QueryParams.QuickOrderSearchResultPageNumber), 10) || 1);
const searchFilters: { name: string, value: number }[] = [
    { name: t('Pages.PartnerPortal.QuickOrder.QuickOrderPage.SearchFilterAll'), value: QuickOrderSearchFilter.All },
    { name: t('Pages.PartnerPortal.QuickOrder.QuickOrderPage.SearchFilterProducts'), value: QuickOrderSearchFilter.Products },
    { name: t('Pages.PartnerPortal.QuickOrder.QuickOrderPage.SearchFilterSpareparts'), value: QuickOrderSearchFilter.Spareparts }
];
const selectedSearchFilter = ref<{ name: string, value: number }>(searchFilters[0]);
const searchActive = ref<boolean>(false);
const asyncActive = ref<boolean>(false);
const searchResults = ref<QuickOrderSearchViewObject[]>([]);
const totalPages = ref<number>(0);

let previousSearchTerm: string = QueryParamsService.getParam(QueryParams.QuickOrderSearchTerm) || null;
const previousSearchProductId: string = QueryParamsService.getParam(QueryParams.QuickOrderSearchProductId) || null;
let previousSearchFilterValue: number = parseInt(QueryParamsService.getParam(QueryParams.QuickOrderSearchResultPageNumber), 10) || null;

const finishedSapLookup = ref<{ [key: string]: boolean }>({});
const showFileUpload = ref<boolean>(false);

const router = useRouter();

function onRelativeUrlChange(route: RelativeUrl, oldRoute: RelativeUrl) {
    if (route && oldRoute && route.path === oldRoute.path) {
        search();
    }
}
watchRelativeUrl(onRelativeUrlChange);

function toggleDate() {
    searchDeliverySort.value = !searchDeliverySort.value;
    updateQueryParams();
}

function updateQueryParams(term:SearchSuggestViewObject = undefined): void {
    if (term !== undefined) {
        searchTerm.value = term.name;
        searchProductId.value = term.productId;
    }
    const searchIsValid = searchParamsAreValid();
    const filterHasChanged = (previousSearchTerm !== null && previousSearchTerm !== searchTerm.value) ||
        (previousSearchFilterValue !== null && previousSearchFilterValue !== selectedSearchFilter.value.value) ||
        (previousSearchProductId !== null && previousSearchProductId !== searchProductId.value);

    if (searchIsValid && filterHasChanged) {
        searchResultPageNumber.value = 1;
        searchDeliverySort.value = true;
    }
    let queryParams = [
    { name: QueryParams.QuickOrderSearchTerm, value: searchTerm.value },
    { name: QueryParams.QuickOrderSearchProductId, value: searchProductId.value },
    { name: QueryParams.QuickOrderSearchFilter, value: selectedSearchFilter.value.value.toString() },
    { name: QueryParams.QuickOrderSearchResultPageNumber, value: searchResultPageNumber.value.toString() }
];

if (props.isUsMarket) {
    queryParams.push({
        name: QueryParams.QuickOrderSearchDeliverySort,
        value: searchDeliverySort.value.toString()
    });
}

let query = QueryParamsService.appendQueryParams(queryParams);
    router.replace({ query });
};

function searchParamsAreValid(): boolean {
    return !(!searchTerm.value || !searchTerm.value.length);
}

function asyncMode(response: QuickOrderSearchViewModel): boolean {
    if (response.searchResults.length === 0) { return false; }

    if (response.searchResults[0].purchasePrice) { return false; }

    return true;
}

function search(): void {
    accumulatedSearchResults = [];

    searchTerm.value = QueryParamsService.getParam(QueryParams.QuickOrderSearchTerm);

    if (!searchTerm.value || !searchTerm.value.length) { return; }

    searchProductId.value = QueryParamsService.getParam(QueryParams.QuickOrderSearchProductId);

    selectedSearchFilter.value = searchFilters[0];

    const searchFilterParam = QueryParamsService.getParam(QueryParams.QuickOrderSearchFilter);
    const searchFilter = parseInt(searchFilterParam, 10);

    if (!isNaN(searchFilter)) {
        const searchFilterReference = searchFilters.find(x => x.value === searchFilter);

        if (searchFilterReference) { selectedSearchFilter.value = searchFilterReference; }
    }

    searchResults.value = null;
    searchActive.value = true;
    asyncActive.value = false;

    const request: QuickOrderSearchWebRequest = {
        filter: selectedSearchFilter.value.value,
        term: searchTerm.value,
        page: searchResultPageNumber.value,
        productId: searchProductId.value,
        deliverySort: searchDeliverySort.value
    };

    Http.cancelableRequest('POST', 'quickorder/search', 'quickOrderSearch', request).then((response: QuickOrderSearchViewModel) => {
        if (response.searchResults === null) { response.searchResults = []; }
        searchResults.value = response.searchResults;
        totalPages.value = response.totalPages;
        const asyncModeValue = asyncMode(response);
        asyncActive.value = asyncModeValue;

        finishedSapLookup.value = response.searchResults.reduce(
            (prev: { [key: string]: boolean }, line: QuickOrderSearchViewObject) => {
                prev[line.variantId] = !asyncModeValue;
                return prev;
            }, {});
        previousSearchTerm = request.term;
        previousSearchFilterValue = request.filter;
        if (asyncModeValue) {
            updateSalesInfo(searchDeliverySort.value);
        }
    }).finally(() => {
        if (!asyncActive.value) {
            searchActive.value = false;
        }
    });
}

let accumulatedSearchResults = [];
async function updateSalesInfo(sortByDelivery): Promise<void> {
    const chunkSize = 25;
    const requests: SalesInfoWebRequest[] = [];

    for (let i = 0; i < Math.ceil(searchResults.value.length / chunkSize); i++) {
        requests.push({
            sapIds: searchResults.value.slice(i * chunkSize, (i + 1) * chunkSize).map(x => x.variantId),
            sortByDeliveryAsc: sortByDelivery
        });
    }
    for (let j = 0; j < requests.length; j++) {
        const request = requests[j];
        const sapLookupResult = Object.assign({}, finishedSapLookup.value);
        await Http.post('salesinfo/BatchedSalesInfo?', request).then((response: SalesInfoViewModel) => {
            // Order searchResults in the same sorted order as the response from the API
            const reorderedResults = response.variants
                .map(salesInfo => searchResults.value.find(x => x.variantId === salesInfo.variantId))
                .filter(row => row !== undefined);

            accumulatedSearchResults.push(...reorderedResults);
            request.sapIds.forEach(id => {
                const salesInfo = response.variants.find(x => x.variantId === id);
                if (salesInfo) {
                    const row = accumulatedSearchResults.find(x => x.variantId === id);
                    if (row) {
                        sapLookupResult[row.variantId] = true;
                        row.delivery = salesInfo.delivery;
                        row.stock = salesInfo.stock;
                        row.priceRetail = salesInfo.priceRetail;
                        row.discount = salesInfo.discount;
                        row.discountPercentage = salesInfo.discountPercentage;
                        row.additionalDiscount = salesInfo.additionalDiscount;
                        row.purchasePrice = salesInfo.purchasePrice;
                        row.hasPrice = salesInfo.hasPrice;
                        row.isNeoVariant = salesInfo.isNeoVariant;

                        if (!row.description) {
                            row.description = salesInfo.description;
                        }
                    }
                } else {
                    // Remove entire result if there is no SAP result
                    accumulatedSearchResults = accumulatedSearchResults.filter(result => result.variantId !== id);

                }
            });
        });
        finishedSapLookup.value = sapLookupResult;
    }
    searchResults.value = accumulatedSearchResults;
    searchActive.value = false;
}

function goToPreviousPage() {
    if (searchResultPageNumber.value > 1) {
        searchResultPageNumber.value--;
    }
    accumulatedSearchResults = [];
    updateQueryParams();
    setTimeout(ScrollService.scrollSearchResultIntoView, 100);
}

function goToNextPage() {
    if (searchResultPageNumber.value < totalPages.value) {
        searchResultPageNumber.value++;
    }
    accumulatedSearchResults = [];
    updateQueryParams();
    setTimeout(ScrollService.scrollSearchResultIntoView, 100);
}

const resultMode = computed<QuickOrderResultMode>(() => {
    if (searchActive.value === true) { return QuickOrderResultMode.LoadingResults; }

    if (searchResults.value && searchResults.value.length > 0) {
        return QuickOrderResultMode.HasResults;
    } else if (searchResults.value && searchResults.value.length === 0) {
        return QuickOrderResultMode.NoResults;
    }

    return QuickOrderResultMode.NoSearch;
});

const hasResults = computed<boolean>(() => {
    return resultMode.value === QuickOrderResultMode.HasResults;
});

const hasNoResults = computed<boolean>(() => {
    return resultMode.value === QuickOrderResultMode.NoResults;
});

const isLoadingResults = computed<boolean>(() => {
    return resultMode.value === QuickOrderResultMode.LoadingResults;
});


return { __sfc: true,QuickOrderResultMode, props, t, initialSort, searchTerm, searchProductId, searchDeliverySort, searchResultPageNumber, searchFilters, selectedSearchFilter, searchActive, asyncActive, searchResults, totalPages, previousSearchTerm, previousSearchProductId, previousSearchFilterValue, finishedSapLookup, showFileUpload, router, onRelativeUrlChange, toggleDate, updateQueryParams, searchParamsAreValid, asyncMode, search, accumulatedSearchResults, updateSalesInfo, goToPreviousPage, goToNextPage, resultMode, hasResults, hasNoResults, isLoadingResults, QuickOrderTable, QuickOrderSearchField }
}

})